<template>
  <popup-modal ref="popup" @click="_cancel">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{ $t("select_sizes") || "Select Sizes" }}</h3>
        <button @click="_cancel" class="close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <p class="item-name">{{ itemName }}</p>
        
        <div class="sizes-container">
          <div 
            v-for="(size, index) in sizes" 
            :key="index"
            class="size-item"
          >
            <div class="size-info">
              <div class="size-label-wrapper">
                <span class="size-label" :title="size.is_size">{{ size.is_size }}</span>
              </div>
              <div class="size-price-wrapper">
                <span class="size-price" :title="formatNumber(size.is_price) + ' ' + currencyName">
                  {{ formatNumber(size.is_price) }} {{ currencyName }}
                </span>
              </div>
            </div>
            <div class="quantity-controls">
              <button 
                @click="decreaseQuantity(index)"
                class="quantity-btn"
                :disabled="selectedSizes[index].quantity <= 0"
                :aria-label="$t('decrease') || 'Decrease'"
              >
                <i class="fas fa-minus"></i>
              </button>
              <input 
                type="number" 
                v-model.number="selectedSizes[index].quantity"
                min="0"
                class="quantity-input"
                @input="validateQuantity(index)"
                :aria-label="$t('quantity') || 'Quantity'"
              />
              <button 
                @click="increaseQuantity(index)"
                class="quantity-btn"
                :aria-label="$t('increase') || 'Increase'"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
        
        <div class="total-info" v-if="totalQuantity > 0">
          <p class="total-text">
            {{ $t("total_quantity") || "Total Quantity" }}: <strong>{{ totalQuantity }}</strong>
          </p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="_cancel" class="btn-cancel">
          {{ $t("cancel") || "Cancel" }}
        </button>
        <button 
          @click="_confirm" 
          class="btn-confirm"
          :disabled="totalQuantity <= 0"
        >
          {{ $t("confirm") || "Confirm" }}
        </button>
      </div>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from "./PopupModal.vue";
import { formatNumber } from "../../utils/functions";

export default {
  name: "SizeSelectionModal",
  components: { PopupModal },
  
  data() {
    return {
      sizes: [],
      selectedSizes: [],
      itemName: "",
      currencyName: "",
      resolvePromise: null,
      rejectPromise: null,
    };
  },
  
  computed: {
    totalQuantity() {
      return this.selectedSizes.reduce((sum, size, index) => {
        if (size.quantity > 0 && this.sizes[index]) {
          // Extract numeric value from size (e.g., "30ml" -> 30)
          const sizeValue = parseFloat(this.sizes[index].is_size.replace(/[^0-9.]/g, '')) || 0;
          return sum + (size.quantity * sizeValue);
        }
        return sum;
      }, 0);
    },
  },
  
  methods: {
    show(opts = {}) {
      this.itemName = opts.itemName || "";
      this.sizes = opts.sizes || [];
      this.currencyName = opts.currencyName || "";
      
      // Initialize selected sizes with quantity 0
      this.selectedSizes = this.sizes.map(() => ({ quantity: 0 }));
      
      this.$refs.popup.open();
      
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    
    hide() {
      this.$refs.popup.close();
    },
    
    _confirm() {
      if (this.totalQuantity <= 0) {
        return;
      }
      
      // Return selected sizes with quantities > 0
      const selected = this.selectedSizes
        .map((sel, index) => ({
          ...this.sizes[index],
          quantity: sel.quantity,
        }))
        .filter(sel => sel.quantity > 0);
      
      this.$refs.popup.close();
      if (this.resolvePromise) {
        this.resolvePromise(selected);
      }
    },
    
    _cancel() {
      this.$refs.popup.close();
      if (this.resolvePromise) {
        this.resolvePromise(null);
      }
    },
    
    increaseQuantity(index) {
      if (this.selectedSizes[index]) {
        this.selectedSizes[index].quantity++;
      }
    },
    
    decreaseQuantity(index) {
      if (this.selectedSizes[index] && this.selectedSizes[index].quantity > 0) {
        this.selectedSizes[index].quantity--;
      }
    },
    
    validateQuantity(index) {
      if (this.selectedSizes[index].quantity < 0) {
        this.selectedSizes[index].quantity = 0;
      }
    },
    
    formatNumber,
  },
};
</script>

<style scoped>
.modal-content {
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 600px;
  width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  margin: 20px auto;
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 95%;
    width: 95%;
    margin: 10px auto;
    max-height: 95vh;
  }
}

.dark .modal-content {
  background: #1f2937;
  color: #f9fafb;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .modal-header {
    padding: 16px 20px;
  }
}

.dark .modal-header {
  border-bottom-color: #374151;
}

.modal-title {
  font-size: 1.375rem;
  font-weight: 700;
  margin: 0;
  color: #111827;
  word-wrap: break-word;
  overflow-wrap: break-word;
  flex: 1;
  min-width: 0;
  padding-right: 12px;
}

@media (max-width: 640px) {
  .modal-title {
    font-size: 1.125rem;
  }
}

.dark .modal-title {
  color: #f9fafb;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 5px;
  line-height: 1;
  transition: color 0.2s;
}

.close-button:hover {
  color: #111827;
}

.dark .close-button {
  color: #9ca3af;
}

.dark .close-button:hover {
  color: #f9fafb;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

@media (max-width: 640px) {
  .modal-body {
    padding: 20px 16px;
  }
}

.item-name {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #374151;
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
}

.dark .item-name {
  color: #d1d5db;
}

.sizes-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.size-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  gap: 16px;
  flex-wrap: wrap;
}

@media (max-width: 640px) {
  .size-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.size-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.dark .size-item {
  background: #111827;
  border-color: #374151;
}

.dark .size-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
}

.size-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

@media (max-width: 640px) {
  .size-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }
}

.size-label-wrapper {
  min-width: 0;
  flex: 0 0 auto;
}

.size-label {
  font-weight: 600;
  font-size: 1.125rem;
  color: #111827;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .size-label {
  color: #f9fafb;
}

.size-price-wrapper {
  min-width: 0;
  flex: 0 0 auto;
}

.size-price {
  font-size: 0.9375rem;
  color: #6b7280;
  font-weight: 500;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .size-price {
  color: #9ca3af;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .quantity-controls {
    width: 100%;
    justify-content: center;
    margin-left: 0;
  }
}

.quantity-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
}

.dark .quantity-btn {
  background: #1f2937;
  border-color: #4b5563;
  color: #d1d5db;
}

.quantity-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.dark .quantity-btn:hover:not(:disabled) {
  background: #374151;
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-input {
  width: 56px;
  padding: 6px 8px;
  text-align: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 600;
}

.dark .quantity-input {
  background: #1f2937;
  border-color: #4b5563;
  color: #f9fafb;
}

.quantity-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

.total-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.dark .total-info {
  border-top-color: #374151;
}

.total-text {
  font-size: 1rem;
  color: #374151;
  margin: 0;
}

.dark .total-text {
  color: #d1d5db;
}

.total-text strong {
  color: #3b82f6;
  font-size: 1.125rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

@media (max-width: 640px) {
  .modal-footer {
    flex-direction: column-reverse;
  }
  
  .modal-footer button {
    width: 100%;
  }
}

.dark .modal-footer {
  border-top-color: #374151;
}

.btn-cancel,
.btn-confirm {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 100px;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.dark .btn-cancel {
  background: #374151;
  color: #d1d5db;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.dark .btn-cancel:hover {
  background: #4b5563;
}

.btn-confirm {
  background: #3b82f6;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #2563eb;
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
